import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  User01Icon,
  Phone01Icon,
  CalendarIcon,
  ClockCheckIcon,
  MessageDotsCircleIcon,
  Flag01Icon,
  Tag01Icon,
  Stars02Icon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { transformTags } from '@/lib/tags'
import { formatConversationDateTime } from '@/lib/dateFormat'
import { DurationDisplay } from './DurationDisplay'
import { OutcomeBadge } from './OutcomeBadge'
import { ChannelIcons } from './ChannelIcons'
import { TagList, TagSelector } from '@/components/common'
import { useUpdateConversation } from '@/hooks/api/useConversations'
import { usePermissions } from '@/hooks/api'
import { toast } from 'sonner'
import type { Conversation } from '@/types/conversation'

interface ConversationDetailsProps {
  conversation: Conversation
  assistantId?: number
  assistantName?: string
  assistantTimezone?: string
  className?: string
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}:</span>
      </div>
      <div className="ml-6">{children}</div>
    </div>
  )
}

export function ConversationDetails({
  conversation,
  assistantId,
  assistantName,
  assistantTimezone,
  className,
}: ConversationDetailsProps) {
  const { t } = useTranslation()
  const { canManage } = usePermissions()
  const updateConversation = useUpdateConversation()
  const [localTags, setLocalTags] = useState<string[]>(conversation.tags || [])

  // Update local tags when conversation changes
  useEffect(() => {
    setLocalTags(conversation.tags || [])
  }, [conversation.tags])

  const handleTagsChange = async (newTags: string[]) => {
    setLocalTags(newTags)
    
    try {
      await updateConversation.mutateAsync({
        id: conversation.id,
        data: { tags: newTags },
      })
      toast.success(t('resources.conversations.tags_updated'))
    } catch (error) {
      // Revert on error
      setLocalTags(conversation.tags || [])
      toast.error(t('resources.conversations.tags_update_failed'))
      console.error('Error updating tags:', error)
    }
  }

  const formattedDate = conversation.created_at
    ? formatConversationDateTime(conversation.created_at, assistantTimezone)
    : t('resources.conversations.unknown')

  // Check if user can edit tags (admin or partner)
  const canEditTags = canManage

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {/* Summary section */}
      <section>
        <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.conversations.fields.summary')}
        </h3>
        <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {conversation.summary || t('resources.conversations.no_summary')}
          </p>
        </div>
      </section>

      {/* Details section */}
      <section>
        <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.conversations.fields.details')}
        </h3>
        <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {/* Left column */}
          <div>
            <DetailRow icon={User01Icon} label={t('resources.conversations.fields.from_name')}>
              <span className="text-gray-900 dark:text-gray-100">
                {conversation.from_name || t('resources.conversations.unknown')}
              </span>
            </DetailRow>

            <DetailRow icon={Phone01Icon} label={t('resources.conversations.fields.phone_number')}>
              {conversation.from_id ? (
                <a
                  href={`tel:${conversation.from_id}`}
                  className="text-brand-600 hover:underline dark:text-brand-400"
                >
                  {conversation.from_id}
                </a>
              ) : (
                <span className="text-gray-500">{t('resources.conversations.unknown')}</span>
              )}
            </DetailRow>

            <DetailRow icon={CalendarIcon} label={t('resources.conversations.fields.date')}>
              <span className="text-gray-900 dark:text-gray-100">
                {formattedDate}
              </span>
            </DetailRow>

            <DetailRow icon={ClockCheckIcon} label={t('resources.conversations.fields.duration')}>
              <DurationDisplay
                seconds={conversation.duration}
                className="text-gray-900 dark:text-gray-100"
              />
            </DetailRow>
          </div>

          {/* Right column */}
          <div>
            <DetailRow icon={Stars02Icon} label={t('resources.conversations.fields.assistant')}>
              {assistantId && assistantName ? (
                <Link
                  to={`/assistants/${assistantId}`}
                  className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                >
                  {assistantName}
                </Link>
              ) : (
                <span className="text-gray-900 dark:text-gray-100">
                  {assistantName || t('resources.conversations.unknown')}
                </span>
              )}
            </DetailRow>

            <DetailRow icon={MessageDotsCircleIcon} label={t('resources.conversations.fields.topic')}>
              <span className="text-gray-900 dark:text-gray-100">
                {conversation.topic || t('resources.conversations.unknown')}
              </span>
            </DetailRow>

            <DetailRow icon={Phone01Icon} label={t('resources.conversations.fields.channels')}>
              <ChannelIcons meta={conversation.meta} twilioId={conversation.twilio_id} />
            </DetailRow>

            <DetailRow icon={Flag01Icon} label={t('resources.conversations.fields.outcome')}>
              {conversation.outcome && (
                <OutcomeBadge outcome={conversation.outcome} showLabel />
              )}
            </DetailRow>

            <DetailRow icon={Tag01Icon} label={t('resources.conversations.fields.tags')}>
              {canEditTags ? (
                <TagSelector
                  selectedTags={localTags}
                  onTagsChange={handleTagsChange}
                />
              ) : (
                localTags && localTags.length > 0 ? (
                  <TagList 
                    tags={transformTags(localTags)} 
                    variant="colored"
                    size="sm"
                  />
                ) : (
                  <span className="text-gray-500">{t('resources.conversations.no_tags')}</span>
                )
              )}
            </DetailRow>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ConversationDetails
